import { TaxonomyFacet, TaxonomyTermStatus } from '@edgebox/sync-core-data-definitions';

/**
 * Disclosing the rivals a 100% share-of-voice number hides.
 *
 * A monopoly reading is the one number on this screen that can mislead by being CORRECT. When an
 * answer names three competitors the customer does not track, the tracked population is a single
 * brand, so its share of voice is genuinely 100% — and it reads as "we own this conversation" when
 * the truth is "we are the only one being counted".
 *
 * Most of those rivals are not unknown to the customer. They are terms that were ARCHIVED and keep
 * accumulating sightings by design, which is why the disclosure links to the term and says how many
 * runs it has been seen in: re-approving it is what makes the share of voice competitive again.
 */

/** One untracked co-subject, as the run mirror records it. */
export interface UntrackedRival {
  name: string;
  norm?: string;
  unitCount?: number;
}

/** A taxonomy term an untracked name turned out to match. */
export interface RivalTermMatch {
  termId?: string;
  /** The term's key, which is what a link to its page carries wherever it has one. */
  termKey?: string;
  facet?: TaxonomyFacet;
  status?: TaxonomyTermStatus;
  /** Runs this term has been seen in, from its accumulated suggestion stats. */
  seenInRuns?: number;
}

/** An untracked rival, resolved against the customer's taxonomy. */
export interface DisclosedRival {
  name: string;
  match?: RivalTermMatch;
  /**
   * Whether to prompt re-approving this term.
   *
   * The actionable insight the chip exists to surface: an archived term still being named in
   * answers is a rival the customer already knows about and stopped counting.
   */
  reconsider: boolean;
}

/** A share of voice that reads as a monopoly. */
export const MONOPOLY_SHARE_PERCENT = 100;

/**
 * How many accumulated runs make an archived term's sightings worth reconsidering.
 *
 * A judgement, not a measurement: a term seen in a handful of runs since archiving is noise, while
 * one seen in dozens is a rival the customer is actively not counting. Set low enough to catch the
 * real cases (the audit found archived rivals at 193, 232 and 506 runs) and high enough that a
 * single stray sighting does not prompt anyone. Worth revisiting against real data.
 */
export const RECONSIDER_MIN_RUNS = 10;

/**
 * The normalization used to match a cited name against a term.
 *
 * The same fold the co-subject mirror applies, so a name stored by the mirror and a term name typed
 * by an operator meet in the same form.
 */
export function foldRivalName(value: string | undefined): string {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

/**
 * Whether the disclosure belongs beside this figure.
 *
 * Only at a monopoly: below 100% the number already shows the customer is not alone, so the chip
 * would be noise. And only when there is something to disclose.
 */
export function shouldDiscloseUntrackedRivals(sharePercent: number | undefined, rivals: readonly UntrackedRival[]): boolean {
  return sharePercent === MONOPOLY_SHARE_PERCENT && distinctRivals(rivals).length > 0;
}

/** The distinct rivals, folded, in the order first seen. */
export function distinctRivals(rivals: readonly UntrackedRival[]): UntrackedRival[] {
  const seen = new Set<string>();
  const distinct: UntrackedRival[] = [];

  for (const rival of rivals) {
    const folded = foldRivalName(rival?.norm || rival?.name);
    if (!folded || seen.has(folded)) {
      continue;
    }
    seen.add(folded);
    distinct.push(rival);
  }

  return distinct;
}

/**
 * Resolve each rival against the customer's terms.
 *
 * A name matching no term stays plain text with no match attached — there is nothing true to say
 * about it beyond that the answer named it, and inventing a count would be worse than silence.
 */
export function disclosedRivals(
  rivals: readonly UntrackedRival[],
  termsByFoldedName: ReadonlyMap<string, RivalTermMatch>
): DisclosedRival[] {
  return distinctRivals(rivals).map((rival) => {
    const match = termsByFoldedName.get(foldRivalName(rival.norm || rival.name));
    return {
      name: rival.name,
      ...(match ? { match } : {}),
      reconsider: match?.status === TaxonomyTermStatus.Archived && (match.seenInRuns ?? 0) >= RECONSIDER_MIN_RUNS,
    };
  });
}

/** "+3 untracked rivals" — the chip's own text. */
export function untrackedRivalChipLabel(count: number): string {
  return `+${count} untracked ${count === 1 ? 'rival' : 'rivals'}`;
}
